/* global globalThis */

import { ref } from 'vue';
import PostSaleDeedFeedbackAPI from 'dashboard/api/postSaleDeedFeedback';

const idempotencyKey = () =>
  globalThis.crypto?.randomUUID?.() ||
  `deed-feedback-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const requestErrorMessage = (requestError, fallback) =>
  requestError?.response?.data?.errors?.[0] ||
  requestError?.response?.data?.message ||
  requestError?.response?.data?.error ||
  fallback;

export function usePostSaleDeedFeedback({ t }) {
  const catalog = ref(null);
  const confirmation = ref(false);
  const error = ref('');
  const loading = ref(false);
  const open = ref(false);
  const pending = ref([]);
  const proof = ref(null);
  const selected = ref(null);
  const showList = ref(false);
  const submitting = ref(false);
  const uploading = ref(false);
  const submissionKey = ref('');
  let sequence = 0;

  const load = async () => {
    sequence += 1;
    const request = sequence;
    error.value = '';
    loading.value = true;
    try {
      const [pendingResponse, catalogResponse] = await Promise.all([
        PostSaleDeedFeedbackAPI.pending(),
        PostSaleDeedFeedbackAPI.questions(),
      ]);
      if (request !== sequence) return;

      pending.value = pendingResponse.data?.data || [];
      catalog.value = catalogResponse.data?.data || null;
      selected.value = pending.value.length === 1 ? pending.value[0] : null;
      showList.value = pending.value.length > 1;
      open.value = pending.value.length > 0;
    } catch (requestError) {
      if (request !== sequence) return;

      open.value = false;
      if (requestError?.response?.status !== 403) {
        error.value =
          requestError?.response?.data?.message ||
          t('POST_SALE.DEED_FEEDBACK.ERROR.LOAD');
      }
    } finally {
      if (request === sequence) loading.value = false;
    }
  };

  const select = id => {
    selected.value =
      pending.value.find(item => String(item.processo_id) === String(id)) ||
      null;
    showList.value = selected.value === null;
    proof.value = null;
    submissionKey.value = '';
  };

  const backToList = () => {
    if (pending.value.length > 1) showList.value = true;
  };

  const upload = async file => {
    if (!selected.value || !file) return null;

    uploading.value = true;
    error.value = '';
    try {
      const { data } = await PostSaleDeedFeedbackAPI.upload(
        selected.value.processo_id,
        file
      );
      proof.value = data.data;
      return proof.value;
    } catch (requestError) {
      error.value = requestErrorMessage(
        requestError,
        t('POST_SALE.DEED_FEEDBACK.ERROR.UPLOAD')
      );
      return null;
    } finally {
      uploading.value = false;
    }
  };

  const submit = async responses => {
    if (!selected.value || submitting.value) return null;

    submitting.value = true;
    error.value = '';
    submissionKey.value ||= idempotencyKey();
    try {
      const result = await PostSaleDeedFeedbackAPI.submit(
        selected.value.processo_id,
        responses,
        submissionKey.value
      );
      confirmation.value = true;
      submissionKey.value = '';
      return result.data?.data;
    } catch (requestError) {
      error.value = requestErrorMessage(
        requestError,
        t('POST_SALE.DEED_FEEDBACK.ERROR.SUBMIT')
      );
      return null;
    } finally {
      submitting.value = false;
    }
  };

  const continueAfterConfirmation = async () => {
    confirmation.value = false;
    proof.value = null;
    await load();
  };

  return {
    catalog,
    backToList,
    confirmation,
    continueAfterConfirmation,
    error,
    load,
    loading,
    open,
    pending,
    proof,
    select,
    selected,
    showList,
    submit,
    upload,
    uploading,
    submitting,
  };
}
